import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Loader2, Building2, TrendingUp, TrendingDown, Users, Clock, Wallet,
  ArrowUpRight, ChevronRight, BarChart3, UserCog, Settings, FileText,
  CheckCheck, Plane, Receipt, KeyRound, DollarSign,
} from "lucide-react";

const RO_MONTHS = ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "nov", "dec"];

function fmtMoney(n) {
  return (n || 0).toLocaleString("ro-RO", { maximumFractionDigits: 0 });
}
function fmtHours(h) {
  return (h || 0).toLocaleString("ro-RO", { maximumFractionDigits: 1 });
}
function fmtPct(p) {
  return `${(p || 0).toFixed(1)}%`;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [data, setData] = useState({
    summary: null,
    projects: [],
    users: [],
    pendingTimesheet: 0,
    pendingTimeoff: 0,
    pendingExpense: 0,
    pendingAccess: 0,
    expenseTotals: { paid: 0, requested: 0, pending: 0 },
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [sumR, projR, usersR, tsPendR, toPendR, expAllR, accPendR] = await Promise.all([
          api.get(`/staff/reports/summary?month=${monthStr}`).catch(() => ({ data: { projects: [], totals: {} } })),
          api.get("/projects").catch(() => ({ data: [] })),
          api.get("/staff/users").catch(() => ({ data: [] })),
          api.get("/staff/timesheet/pending").catch(() => ({ data: [] })),
          api.get("/timeoff/requests/all?status=pending_manager").catch(() => ({ data: [] })),
          api.get("/expenses/reports/all").catch(() => ({ data: [] })),
          api.get("/access-requests/all").catch(() => ({ data: [] })).catch(() => ({ data: [] })),
        ]);

        // Extra timeoff query: pending_hr
        const toHrR = await api.get("/timeoff/requests/all?status=pending_hr").catch(() => ({ data: [] }));

        // Expense totals
        const paid = expAllR.data.reduce((s, r) => s + (r.totals?.paid_ron || 0), 0);
        const requested = expAllR.data.reduce((s, r) => s + (r.totals?.requested_ron || 0), 0);
        const pendingCount = expAllR.data.filter(r =>
          ["submitted", "info_requested", "manager_approved", "finance_verified", "approved_for_payment"].includes(r.status)
        ).length;

        // Access pending
        const accessPending = (accPendR.data || []).filter(r =>
          ["submitted", "manager_approved"].includes(r.status)
        ).length;

        setData({
          summary: sumR.data,
          projects: projR.data,
          users: usersR.data.filter(u => u.role !== "client"),
          pendingTimesheet: (tsPendR.data || []).length,
          pendingTimeoff: (toPendR.data || []).length + (toHrR.data || []).length,
          pendingExpense: pendingCount,
          pendingAccess: accessPending,
          expenseTotals: { paid, requested, pending: pendingCount },
        });
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [monthStr]);

  const firstName = (user?.name || "").split(" ")[0] || "";
  const totals = data.summary?.totals || {};
  const activeProjects = data.projects.filter(p => p.status === "active" || p.status === "in_progress");
  const topProjects = [...(data.summary?.projects || [])]
    .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
    .slice(0, 6);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px] mx-auto">
        {/* Greeting */}
        <header className="pb-8 pt-2">
          <div className="text-[14px] font-display font-medium text-ink-muted mb-3">
            {now.toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · Vedere centralizată
          </div>
          <h1 className="text-[42px] md:text-[56px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            Bună, {firstName}.
          </h1>
          <p className="text-ink-muted mt-3 text-[15px] max-w-2xl">
            Panou administrativ — indicatori financiari, activitate angajați și cereri de aprobat la nivel de organizație.
          </p>
        </header>

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
          </div>
        ) : (
          <>
            {/* Financial KPIs */}
            <section className="mb-12">
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <div className="section-eyebrow">
                    Financiar — {RO_MONTHS[now.getMonth()]} {now.getFullYear()}
                  </div>
                  <div className="section-title">Performanță organizațională</div>
                </div>
                <Link to="/staff/rapoarte" data-testid="link-full-reports"
                  className="text-[13px] text-ink-muted hover:text-ink inline-flex items-center gap-1">
                  Rapoarte complete <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 pt-4">
                <KpiCard
                  label="Venituri lunare"
                  metric={`${fmtMoney(totals.revenue)} RON`}
                  sub={`${fmtHours(totals.hours)} ore pontate`}
                  variant="v-coral"
                  icon={DollarSign}
                />
                <KpiCard
                  label="Cost total"
                  metric={`${fmtMoney(totals.total_cost)} RON`}
                  sub="salarii + directe + indirecte"
                  variant="v-magenta"
                  icon={Wallet}
                />
                <KpiCard
                  label="Marjă"
                  metric={`${fmtMoney(totals.margin)} RON`}
                  sub={fmtPct(totals.margin_pct)}
                  variant="v-violet"
                  icon={totals.margin >= 0 ? TrendingUp : TrendingDown}
                />
                <KpiCard
                  label="Deconturi"
                  metric={`${fmtMoney(data.expenseTotals.paid)} RON`}
                  sub={`${data.expenseTotals.pending} în așteptare · ${fmtMoney(data.expenseTotals.requested)} solicitate`}
                  variant="v-blue"
                  icon={Receipt}
                />
              </div>
            </section>

            {/* Two column: projects + org snapshot */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6 mb-12">
              {/* Top projects */}
              <section className="surface p-8">
                <div className="flex items-baseline justify-between mb-6">
                  <div>
                    <div className="section-eyebrow">Top proiecte (după venit)</div>
                    <div className="section-title">Performanță pe proiect</div>
                  </div>
                  <Link to="/staff/rapoarte"
                    className="text-[13px] text-ink-muted hover:text-ink inline-flex items-center gap-1">
                    Toate <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
                {topProjects.length === 0 ? (
                  <div className="py-16 text-center text-ink-muted">
                    <Building2 className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
                    <div className="text-[14px]">Nicio activitate pontată luna aceasta.</div>
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-100 -mx-2">
                    {topProjects.map(p => (
                      <div key={p.project_id} className="flex items-center gap-4 py-4 px-2 rounded-lg hover:bg-neutral-50/60 transition-colors">
                        <div className="h-11 w-11 rounded-xl bg-neutral-100 flex items-center justify-center text-ink-muted shrink-0">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[15px] font-semibold text-ink truncate">
                            {p.project_code} — {p.project_name}
                          </div>
                          <div className="text-[12px] text-ink-muted tabular-nums mt-0.5">
                            {fmtHours(p.hours)}h · Cost {fmtMoney(p.total_cost)} · Venit {fmtMoney(p.revenue)} RON
                          </div>
                        </div>
                        <div className={`text-right shrink-0 ${p.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                          <div className="text-[15px] font-display font-semibold tabular-nums">
                            {p.margin >= 0 ? "+" : ""}{fmtMoney(p.margin)} RON
                          </div>
                          <div className="text-[11px] text-ink-muted">{fmtPct(p.margin_pct)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Org snapshot */}
              <section className="surface p-8">
                <div className="mb-6">
                  <div className="section-eyebrow">Organizație</div>
                  <div className="section-title">Sinteză angajați</div>
                </div>
                <div className="divide-y divide-neutral-100">
                  <SnapshotRow icon={Users} label="Angajați activi" value={data.users.length}
                    onClick={() => navigate("/staff/utilizatori")} />
                  <SnapshotRow icon={Building2} label="Proiecte active" value={activeProjects.length}
                    onClick={() => navigate("/staff/rapoarte")} />
                  <SnapshotRow icon={Clock} label="Ore pontate luna curentă" value={fmtHours(totals.hours)}
                    onClick={() => navigate("/staff/rapoarte")} />
                </div>
              </section>
            </div>

            {/* Pending approvals */}
            <section className="mb-12">
              <div className="mb-5">
                <div className="section-eyebrow">Cereri de rezolvat</div>
                <div className="section-title">Aprobări în așteptare</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <PendingCard label="Pontaj" count={data.pendingTimesheet} icon={CheckCheck}
                  to="/staff/aprobari" />
                <PendingCard label="Concedii & absențe" count={data.pendingTimeoff} icon={Plane}
                  to="/staff/aprobari-timeoff" />
                <PendingCard label="Deconturi" count={data.pendingExpense} icon={Wallet}
                  to="/staff/decontari-aprobari" />
                <PendingCard label="Acces proiecte" count={data.pendingAccess} icon={KeyRound}
                  to="/staff/cereri-acces-aprobari" />
              </div>
            </section>

            {/* Quick admin actions */}
            <section>
              <div className="mb-5">
                <div className="section-eyebrow">Administrare</div>
                <div className="section-title">Acțiuni rapide</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <QuickAction to="/staff/utilizatori" icon={UserCog} label="Utilizatori" />
                <QuickAction to="/staff/echipa" icon={Users} label="Proiecte & alocări" />
                <QuickAction to="/staff/rapoarte" icon={BarChart3} label="Rapoarte" />
                <QuickAction to="/staff/timeoff-admin" icon={Settings} label="HR — Concedii & absențe" />
                <QuickAction to="/staff/documente-admin" icon={FileText} label="HR — Documente" />
              </div>
            </section>
          </>
        )}
      </div>
    </PortalLayout>
  );
}


function KpiCard({ label, metric, sub, variant, icon: Icon }) {
  return (
    <div className={`pio-card-vivid ${variant} p-6 flex flex-col min-h-[176px]`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="pio-vivid-icon">
          <Icon />
        </div>
        <div className="text-[13.5px] font-display font-semibold text-white/90">
          {label}
        </div>
      </div>
      <div className="text-[28px] font-display font-bold tracking-[-0.02em] tabular-nums text-white leading-[1.05] mb-2">
        {metric}
      </div>
      <div className="text-[12.5px] text-white/75 mt-auto">{sub}</div>
    </div>
  );
}

function SnapshotRow({ icon: Icon, label, value, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 py-5 border-b border-neutral-100 hover:bg-neutral-50/60 transition-colors text-left px-2 -mx-2 rounded-lg"
    >
      <div className="h-11 w-11 rounded-xl bg-neutral-100 flex items-center justify-center text-ink-muted shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="text-[14px] text-ink font-medium">{label}</div>
      </div>
      <div className="text-[22px] font-display font-semibold text-ink tabular-nums">{value}</div>
      <ChevronRight className="h-4 w-4 text-ink-muted" />
    </button>
  );
}

function PendingCard({ label, count, icon: Icon, to }) {
  const highlight = count > 0;
  return (
    <Link to={to}
      className={`block p-6 rounded-2xl border transition-all surface-interactive ${highlight
        ? "bg-amber-50 border-amber-200 hover:bg-amber-100"
        : "bg-white border-neutral-200"}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${highlight ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-ink-muted"}`}>
          <Icon className="h-5 w-5" />
        </div>
        <ChevronRight className={`h-4 w-4 ${highlight ? "text-amber-800" : "text-ink-muted"}`} />
      </div>
      <div className={`text-[38px] font-display font-semibold tabular-nums leading-none mb-1 ${highlight ? "text-amber-900" : "text-ink"}`}>
        {count}
      </div>
      <div className={`text-[13px] ${highlight ? "text-amber-800" : "text-ink-muted"}`}>{label}</div>
    </Link>
  );
}

function QuickAction({ to, icon: Icon, label }) {
  return (
    <Link to={to}
      className="flex items-center gap-3 p-5 rounded-2xl border border-neutral-200 bg-white hover:border-brand hover:shadow-md transition-all surface-interactive">
      <div className="h-11 w-11 rounded-xl bg-brand/8 text-brand flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-[14px] font-semibold text-ink">{label}</div>
    </Link>
  );
}
